import noop from 'lodash/noop';
import { useRouter } from 'next/router';
import type { FunctionComponent, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { StepNavType } from '../../components/step_nav/type';
import { getStepsConfig } from '../../../../steps';
import type { AppData } from '../../../../types/app_data';
import { useStepProgress } from '../../../../steps/utils/progress';

import { StepNav } from './StepNav';
import { StepNav as StepNavGarageOnly } from './StepNavGarageOnly';
import type { StepNavProps } from './type';

interface StepNavContextData {
  stepTitle?: ReactNode;
}

interface StepNavContextType {
  stepNavData: StepNavContextData;
  setStepNavData: (data: Partial<StepNavContextData>) => void;
  progress?: {
    allProgress: number;
    categoryProgress: number;
  };
}

/**
 * A context to share step nav title
 */
export const StepNavContext = createContext<StepNavContextType>({
  stepNavData: {},
  setStepNavData: noop,
});

/**
 * Return the initial context data
 */
export const useInitStepNavContextData = (appData: AppData): StepNavContextType => {
  const STEPS = useMemo(() => getStepsConfig(appData.flowType), [appData.flowType]);
  const [stepNavData, setStepNavData] = useState<StepNavContextData>({});
  const updateStepNavData = useCallback(
    (data: Partial<StepNavContextData>) => setStepNavData((oldData) => ({ ...oldData, ...data })),
    []
  );
  const router = useRouter();
  const currentStep = useMemo(() => {
    return STEPS[appData.stepSection]?.find((step) => step.pathname === router.pathname);
  }, [appData.stepSection, router.pathname, STEPS]);

  const [progress, setProgress] = useState<{ allProgress: number; categoryProgress: number } | undefined>();
  const onProgressChange = useCallback(
    (allProgress: number, categoryProgress: number) => setProgress({ allProgress, categoryProgress }),
    []
  );

  useStepProgress({ stepID: currentStep?.id, appData, onProgressChange });

  const stepNavContextData: StepNavContextType = useMemo(
    () => ({
      stepNavData,
      setStepNavData: updateStepNavData,
      progress,
    }),
    [stepNavData, updateStepNavData, progress]
  );

  return stepNavContextData;
};

export const useStepNavContext = () => useContext(StepNavContext);

export const getStepNav = (stepNavType: StepNavType): FunctionComponent<StepNavProps> | null => {
  switch (stepNavType) {
    case StepNavType.None:
      return null;
    case StepNavType.GarageOnly:
      return StepNavGarageOnly;
    case StepNavType.Default:
    default:
      return StepNav;
  }
};
